import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
  signal,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

export interface Review {
  description: string;
  title: string;
  country: string;
}

let registered = false;

@Component({
  selector: 'app-reviews',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './reviews.component.html',
  styleUrls: ['./reviews.component.scss'],
})
export class ReviewsComponent implements AfterViewInit, OnDestroy {
  @Input({ required: true }) reviews: Review[] = [];

  @ViewChild('root') root?: ElementRef<HTMLElement>;
  @ViewChild('head') head?: ElementRef<HTMLElement>;
  @ViewChild('stage') stage?: ElementRef<HTMLElement>;

  readonly active = signal(0);

  reduced = false;

  private ctx?: gsap.Context;
  private io?: IntersectionObserver;
  private timer?: ReturnType<typeof setInterval>;
  private cleanups: Array<() => void> = [];
  private isBrowser: boolean;
  private visible = false;

  constructor(
    private zone: NgZone,
    private host: ElementRef<HTMLElement>,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  trackReview = (i: number, r: Review) => r.title + i;

  /** Signed distance from the active card, wrapping both ways. */
  offset(i: number): number {
    const n = this.reviews.length;
    if (!n) return 0;
    let d = i - this.active();
    if (d > n / 2) d -= n;
    if (d < -n / 2) d += n;
    return d;
  }

  /** Three on stage: the active one and its immediate neighbours. */
  isOnStage(i: number): boolean {
    return Math.abs(this.offset(i)) <= 1;
  }

  /** Initials for the gradient avatar — the data carries no photos. */
  initials(name: string): string {
    return name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0] ?? '')
      .join('')
      .toUpperCase();
  }

  go(dir: 1 | -1): void {
    const n = this.reviews.length;
    if (!n) return;
    this.active.set((this.active() + dir + n) % n);
    this.restart();
  }

  select(i: number): void {
    if (i === this.active()) return;
    this.active.set(i);
    this.restart();
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!registered) {
      gsap.registerPlugin(ScrollTrigger);
      registered = true;
    }

    this.zone.runOutsideAngular(() => {
      if (!this.reduced) {
        this.ctx = gsap.context(() => this.buildEntrance(), this.host.nativeElement);
      }
      this.watchVisibility();
    });
  }

  private buildEntrance(): void {
    const tl = gsap.timeline({
      defaults: { ease: 'power4.out' },
      scrollTrigger: {
        trigger: this.head!.nativeElement,
        start: 'top 86%',
        once: true,
      },
    });

    // fromTo, never from — see the note in leaders.component.ts. A ScrollTrigger
    // refresh invalidates from() tweens, which then record their own from-state
    // as the destination and animate opacity 0 -> 0.
    const done = { clearProps: 'opacity,transform' };

    tl.fromTo('.rv__badge', { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.7, ...done })
      .fromTo('.rv__title', { opacity: 0, y: 26 }, { opacity: 1, y: 0, duration: 0.8, ...done }, '-=0.44')
      .fromTo('.rv__divider', { opacity: 0, scaleX: 0 }, { opacity: 1, scaleX: 1, duration: 0.6, ...done }, '-=0.5');

    // Cards are laid out and faded by CSS (transform from --o, opacity per
    // is-active/is-off), so the stage enters as one unit rather than leaving an
    // inline transform on every card.
    gsap.fromTo(
      this.stage!.nativeElement,
      { opacity: 0, y: 44 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power4.out',
        clearProps: 'opacity,transform',
        scrollTrigger: {
          trigger: this.stage!.nativeElement,
          start: 'top 88%',
          once: true,
        },
      }
    );
  }

  private watchVisibility(): void {
    const el = this.root?.nativeElement;
    if (!el || typeof IntersectionObserver === 'undefined') {
      this.start();
      return;
    }

    this.io = new IntersectionObserver(
      (entries) => {
        this.visible = entries.some((e) => e.isIntersecting);
        el.classList.toggle('is-paused', !this.visible);
        if (this.visible) this.start();
        else this.stop();
      },
      { threshold: 0 }
    );

    this.io.observe(el);
    this.cleanups.push(() => this.io?.disconnect());

    const enter = () => this.stop();
    const leave = () => this.visible && this.start();
    el.addEventListener('mouseenter', enter, { passive: true });
    el.addEventListener('mouseleave', leave, { passive: true });
    this.cleanups.push(() => {
      el.removeEventListener('mouseenter', enter);
      el.removeEventListener('mouseleave', leave);
    });
  }

  private start(): void {
    if (this.timer || this.reduced || this.reviews.length < 2) return;
    this.zone.runOutsideAngular(() => {
      this.timer = setInterval(() => {
        if (document.hidden) return;
        this.zone.run(() => {
          const n = this.reviews.length;
          this.active.set((this.active() + 1) % n);
        });
      }, 5000);
    });
  }

  private stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = undefined;
  }

  private restart(): void {
    this.stop();
    if (this.visible) this.start();
  }

  ngOnDestroy(): void {
    this.stop();
    this.cleanups.forEach((fn) => fn());
    this.cleanups = [];
    this.io?.disconnect();
    this.ctx?.revert();
  }
}
